/**
 * /customer/redemption/:id — single redemption detail (Phase 2d).
 *
 * Mirror of mobile (customer)/redemption/[id].tsx (lines 35–290) minus
 * the review-prompt modal (operator §6 deferred for web). The page is
 * strictly read-only — no `Use code` CTA, no edit, no manual
 * "mark used" (cashier-side surface, frozen).
 *
 * Backend has no per-id endpoint; we derive the row by filtering
 * `customerRedemptions()` — same approach as mobile (line 63–64).
 */
import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  X, Ticket, Loader2, Info,
} from 'lucide-react';
import { api, resolveImageUrl } from '@/lib/api';
import { qk } from '@/lib/queryKeys';
import { useAuthStore } from '@/lib/auth';

function formatDateTime(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('en-GB', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export default function CustomerRedemptionDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const token = useAuthStore((s) => s.token);

  const listQ = useQuery({
    queryKey: qk.customerRedemptions(),
    queryFn: () => api.customerRedemptions(token),
    enabled: !!token,
  });
  const redemption = useMemo(
    () => (listQ.data ?? []).find((r) => r.id === id) ?? null,
    [listQ.data, id],
  );

  // Countdown — recompute every second on active redemptions so the
  // expiry chip ticks down without manual refresh. Mirror of mobile
  // lines 105–117.
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    if (!redemption || redemption.status !== 'active' || !redemption.expires_at) {
      return undefined;
    }
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [redemption]);

  const remainingText = useMemo(() => {
    if (!redemption?.expires_at) return '';
    const diff = new Date(redemption.expires_at).getTime() - now;
    if (diff <= 0) return 'Expired';
    const hr = Math.floor(diff / 3_600_000);
    const min = Math.floor((diff % 3_600_000) / 60_000);
    const sec = Math.floor((diff % 60_000) / 1000);
    if (hr > 0) return `Expires in ${hr}h ${min}m`;
    if (min > 0) return `Expires in ${min}m ${sec}s`;
    return `Expires in ${sec}s`;
  }, [redemption, now]);

  // ---- Loading / 404 / partial-payload guard ----------------------------
  if (listQ.isLoading) {
    return (
      <div
        className="flex items-center justify-center min-h-[40vh] text-white/45"
        data-testid="customer-redemption-detail-screen"
      >
        <Loader2 size={28} className="animate-spin" />
      </div>
    );
  }
  if (!redemption) {
    return (
      <div
        className="rounded-3xl border border-white/8 bg-[#15151B] p-10 text-center"
        data-testid="customer-redemption-detail-screen"
      >
        <Ticket size={28} className="mx-auto text-gold/55 mb-3" />
        <div className="text-base font-medium">Redemption not found.</div>
        <Link
          to="/customer/redemptions"
          data-testid="customer-redemption-not-found-back"
          className="inline-flex items-center gap-1.5 mt-5 px-4 py-2 rounded-xl bg-gold/15 border border-gold/45 text-gold text-sm font-medium hover:bg-gold/20 transition-colors"
        >
          Back to redemptions
        </Link>
      </div>
    );
  }
  // Defensive partial-payload guard — mirrors mobile lines 145–154.
  if (!redemption.brand || !redemption.reward) {
    return (
      <div
        className="flex flex-col items-center justify-center min-h-[40vh] gap-3 text-white/55"
        data-testid="customer-redemption-loading-details"
      >
        <Loader2 size={26} className="animate-spin text-gold/80" />
        <div className="text-sm">Loading details…</div>
      </div>
    );
  }

  const tint = redemption.brand?.primary_color || '#C9A84C';
  const statusLabel =
    redemption.status === 'active'
      ? remainingText
      : redemption.status === 'used'
      ? 'Used'
      : 'Expired';
  const statusDotClass =
    redemption.status === 'active'
      ? 'bg-emerald-400'
      : 'bg-white/35';

  return (
    <div className="space-y-6" data-testid="customer-redemption-detail-screen">
      {/* Header — close button mirrors mobile X icon */}
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={() => navigate('/customer/redemptions')}
          aria-label="Close"
          data-testid="customer-redemption-close"
          className="w-9 h-9 rounded-full bg-[#15151B] border border-white/10 grid place-items-center text-white/75 hover:text-gold hover:border-gold/35 transition-colors"
        >
          <X size={18} />
        </button>
      </div>

      {/* Brand + reward title row */}
      <div className="flex items-center gap-3">
        {redemption.brand?.logo_url ? (
          <img
            src={resolveImageUrl(redemption.brand.logo_url)}
            alt=""
            className="w-12 h-12 rounded-2xl border border-white/15 bg-black/40 shrink-0"
          />
        ) : (
          <div className="w-12 h-12 rounded-2xl bg-gold/15 border border-gold/30 grid place-items-center text-gold font-bold shrink-0">
            {(redemption.brand?.name ?? '?').slice(0, 1)}
          </div>
        )}
        <div className="min-w-0 flex-1">
          <div
            className="text-[10px] uppercase tracking-[0.16em] font-mono"
            style={{ color: tint }}
          >
            {redemption.brand?.name ?? 'participating merchant'}
          </div>
          <div className="font-display text-lg font-semibold mt-0.5 truncate">
            {redemption.reward?.title ?? 'Reward'}
          </div>
        </div>
      </div>

      {/* Code card — large, monospaced, pulse on active */}
      <div
        className="relative rounded-3xl border border-gold/45 bg-gold/[0.07] px-6 py-7 text-center overflow-hidden"
        data-testid="customer-redemption-code-card"
      >
        <div
          className="pointer-events-none absolute -inset-1 rounded-3xl opacity-50"
          style={{
            background:
              'radial-gradient(ellipse at center, rgba(201,168,76,0.28), transparent 65%)',
          }}
        />
        <div className="relative">
          <div className="text-[10px] font-mono uppercase tracking-[0.22em] text-gold">
            Redemption code
          </div>
          <div
            className="font-mono text-4xl md:text-5xl font-bold tracking-[0.22em] mt-3"
            style={{ color: tint }}
            data-testid="customer-redemption-code-value"
          >
            {redemption.redemption_code}
          </div>
          <div className="mt-4 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-black/45 border border-white/10">
            <span
              className={`w-2 h-2 rounded-full ${statusDotClass}`}
              aria-hidden="true"
            />
            <span className="text-xs font-medium text-white/85">
              {statusLabel}
            </span>
          </div>
        </div>
      </div>

      {/* Details list */}
      <div
        className="rounded-2xl border border-white/8 bg-[#15151B] divide-y divide-white/6"
        data-testid="customer-redemption-details"
      >
        <DetailRow label="Reward" value={redemption.reward?.title ?? 'Reward'} />
        <DetailRow label="Stamps used" value={`${redemption.stamps_used}`} />
        <DetailRow label="Redeemed" value={formatDateTime(redemption.redeemed_at)} />
        {redemption.expires_at ? (
          <DetailRow
            label="Expires"
            value={formatDateTime(redemption.expires_at)}
          />
        ) : null}
        {redemption.used_at ? (
          <DetailRow label="Used" value={formatDateTime(redemption.used_at)} />
        ) : null}
      </div>

      {/* Cashier tip — mobile-verbatim copy */}
      <div className="flex items-start gap-2.5 rounded-2xl border border-gold/25 bg-gold/[0.05] px-4 py-3">
        <Info size={16} className="text-gold mt-0.5 shrink-0" />
        <div className="text-xs text-white/70 leading-relaxed">
          Cashier-side redemption confirmation arrives in a later phase. For
          now, show this code at the counter — it&apos;s valid for 24 hours.
        </div>
      </div>

      {/* CTA — view all redemptions */}
      <Link
        to="/customer/redemptions"
        data-testid="customer-redemption-go-all"
        className="w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-2xl bg-gold/15 border border-gold/45 text-gold text-sm font-semibold hover:bg-gold/25 transition-colors"
      >
        View all redemptions
      </Link>
    </div>
  );
}

function DetailRow({ label, value }) {
  return (
    <div className="flex items-center justify-between px-4 py-3">
      <div className="text-[10px] uppercase tracking-[0.16em] font-mono text-white/45">
        {label}
      </div>
      <div className="text-sm text-white/90 font-medium truncate ml-3 max-w-[60%] text-right">
        {value}
      </div>
    </div>
  );
}
